import React, { useEffect, useState } from "react";
import { Drawer, DrawerRef } from "../../../../components/drawers/Drawer";
import Modal from "../../../../components/base/Modal";
import Indicator from "../../../../components/base/Indicator";
import Icon from "../../../../components/base/Icon";
import { useEnvironment, useHub } from "../../../../core/Global";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import "./SignedInDrawer.css";


interface SignedInUser {
    userId?: string
    name?: string
    username?: string
    avatar?: string
    roleText?: string
    lastActiveAt?: string
    isMe?: boolean
}


// Who is holding the account's user slots right now, behind the "Concurrent users" tile.
//
// The owner and admins can sign anybody else out from here -- every session goes and their open
// screens drop out at once. Never themselves: they are always one of the people listed, and
// signing out is what the menu is for.
export default function SignedInDrawer(props: {
    drawerRef?: React.RefObject<DrawerRef>,
    // Changes when somebody signs in or out; the open list reads itself again.
    refreshKey?: number,
    onClose: () => void,
    onChanged: () => void
}) {

    const hub = useHub()
    const env = useEnvironment()

    // The server sends the avatar as it is stored: a path on the CDN, sometimes a full URL, and
    // the placeholder for somebody without one. Resolved the way Avatar does it everywhere else,
    // so the picture that shows on their profile shows here too.
    const avatarOf = (p: SignedInUser) => {
        const url = p.avatar
        if (!url || url === "/assets/media/avatars/blank.png") return undefined
        return url.startsWith("http") ? url : env.hubUrl + url
    }

    const [data, dataSet] = useState<any>()
    const [loading, loadingSet] = useState<boolean>(true)
    const [target, targetSet] = useState<SignedInUser>()

    const load = () => {
        hub.Get("/accounts/profile/plan/sessions", res => {
            loadingSet(false)
            if (res.success) dataSet(res.data)
            else toastError(res.message ?? "Couldn't load who is signed in.")
        }, () => loadingSet(false))
    }

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (props.refreshKey) load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.refreshKey])

    const people: SignedInUser[] = data?.sessions ?? []
    const canManage = !!data?.canManage

    const ago = (d?: string) => {
        if (!d) return ""
        const minutes = Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 60000))
        return minutes < 1 ? "active now" : `active ${minutes} min ago`
    }

    return (
        <>
            <Drawer ref={props.drawerRef} id="drawer-signed-in"
                title="Signed in now"
                titleIcon="user-group"
                subtitle={data ? `${data.inUse} of ${data.limit} user slots in use` : undefined}
                size="normal"
                onClose={props.onClose}>

                {
                    loading ?
                        <div className="py-20"><Indicator /></div>
                        : people.length === 0 ?
                            <div className="text-center text-gray-500 py-15">Nobody is signed in.</div>
                            :
                            <div className="signed-in-list">
                                {
                                    people.map(p =>
                                        <div key={p.userId} className="signed-in-row">
                                            <div className="signed-in-avatar">
                                                {
                                                    avatarOf(p) ?
                                                        <img src={avatarOf(p)} alt={p.name} />
                                                        : (p.name ?? p.username ?? "?").substring(0, 1).toUpperCase()
                                                }
                                                <span className="signed-in-dot" />
                                            </div>
                                            <div className="signed-in-who">
                                                <div className="signed-in-name">
                                                    {p.name || p.username}
                                                    {p.isMe && <span className="signed-in-me">You</span>}
                                                </div>
                                                <div className="signed-in-meta">{p.roleText} · {ago(p.lastActiveAt)}</div>
                                            </div>
                                            {
                                                canManage && !p.isMe &&
                                                <button type="button" className="btn btn-sm btn-light-danger signed-in-action"
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        targetSet(p);
                                                        window.openModal("modal-force-signout");
                                                    }}>
                                                    <Icon icon="right-from-bracket" type="solid" size={11} className="me-2" />
                                                    Sign out
                                                </button>
                                            }
                                        </div>
                                    )
                                }
                            </div>
                }

                <div className="signed-in-note">
                    <Icon icon="circle-info" type="solid" size={11} />
                    Counted from the last 15 minutes of activity. Signing someone out ends every session
                    they have, and their open screens close straight away.
                </div>

            </Drawer>

            <Modal id="modal-force-signout"
                title={"Sign out " + (target?.name || target?.username || "")}
                buttonText="Sign out" buttonColor="danger"
                onConfirm={callback => {
                    hub.Post("/accounts/profile/plan/sessions/signout", { userId: target?.userId }, res => {
                        if (res.success) {
                            toastSuccess(`${target?.name || target?.username} has been signed out.`)
                            load()
                            props.onChanged()
                        }
                        else toastError(res.message)
                        callback(!res.success)
                    }, () => callback(true))
                }}>
                <p className="mb-0">
                    {target?.name || target?.username} will be signed out on every device right away, and
                    their slot is free for someone else. They can sign in again when there is room.
                </p>
            </Modal>
        </>
    )
}
